// @flow
import React, { PureComponent } from 'react';
import socketIOClient from 'socket.io-client';
import { withHelmet } from 'infrastructure/seo/Helmet';

import RencontreHighlights from 'components/rencontre/general/RencontreHighlights';
import RencontreTeam from 'containers/rencontre/general/RencontreTeamContainer';
import RencontreStatRow from 'components/rencontre/stats/RencontreStatRow';
import Loading from 'components/fragments/Loading';

import type { RencontreType, RencontreActionContainerType } from 'types/Rencontre';
import { getMetaBySlug } from 'utils/seoUtils';

import { getMidMatchScore } from 'utils/rencontreUtils';

import { convertActionFromWS, mergeActions } from 'services/Rencontre';
import { WS_BASE_URL } from 'constants/api';

let hidden = null;
let visibilityChange = null;
if (typeof document.hidden !== 'undefined') {
  hidden = 'hidden';
  visibilityChange = 'visibilitychange';
// $FlowFixMe
} else if (typeof document.msHidden !== 'undefined') {
  hidden = 'msHidden';
  visibilityChange = 'msvisibilitychange';
// $FlowFixMe
} else if (typeof document.webkitHidden !== 'undefined') {
  hidden = 'webkitHidden';
  visibilityChange = 'webkitvisibilitychange';
}


export type DispatchProps = {
  setRencontre: (actions: RencontreActionContainerType[]) => void,
};

export type StateProps = {
  rencontre: ?RencontreType,
};

type Props = StateProps & DispatchProps;

type State = {
  actions: RencontreActionContainerType[],
}

class RencontreGeneralLive extends PureComponent<Props, State> {
  state: State = {
    actions: [],
  };

  socket: ?socketIOClient = null;

  handleVisibilityChange = () => {
    // $FlowFixMe
    if (!document[hidden] && (!this.socket || !this.socket.connected)) {
      /**
       * TODO : devrait être améliorable
       */
      window.location.reload(true);
    }
  };

  componentDidMount() {
    const { rencontre, setRencontre } = this.props;

    if (rencontre) {
      // $FlowFixMe
      document.addEventListener(visibilityChange, this.handleVisibilityChange.bind(this), false);
      this.setState({ actions: rencontre.actions });

      const {
        competitionEquipeLocaleId,
        competitionEquipeVisiteurId,
        id: rencontre_id,
      } = rencontre;

      const socket = socketIOClient(WS_BASE_URL, { transports: ['websocket'] });
      this.socket = socket;

      socket.emit('subscribe', rencontre_id);
      socket.on(rencontre_id, (data) => {
        const newAction = data;
        if (newAction.type === 'MATCHOVER') {
          window.scrollTo(0, 0);
          window.location.reload(true);
        } else {
          const actions = convertActionFromWS(newAction);

          this.setState((prevState) => ({
            actions: mergeActions(
              prevState.actions,
              actions,
              parseInt(competitionEquipeLocaleId, 10),
              parseInt(competitionEquipeVisiteurId, 10)
            ),
          }), () => setRencontre(this.state.actions));
        }
      });
      socket.on('disconnect', () => {
        window.location.reload(true);
      });
    }
  }

  componentWillUnmount() {
    this.socket && this.socket.close();
    // $FlowFixMe
    document.removeEventListener(visibilityChange, this.handleVisibilityChange.bind(this));
  }

  _renderStats = () => {
    const { rencontre } = this.props;

    if (!rencontre) {
      return <Loading />;
    }

    let isLocal = true;
    if (rencontre.resultVisiteur.ourClub) {
      isLocal = false;
    }
    const { actions: actionFiltered } = this.state;
    const {
      dateEffective,
      localStructure: {
        nom: localNom,
      },
      visiteurStructure: {
        nom: visiteurNom,
      },
    } = rencontre;

    const saison = dateEffective.getFullYear();

    return (
      <div className="row mt-5">
        <div className="col col-lg-8on9">
          <h2 className="mb-3">{`Avant match ${localNom} - ${visiteurNom} saison ${saison}`}</h2>
          <h3 className="mb-3">Statistiques du match</h3>
          <div className="stats-match mb-2">
            <div className="stats-match__header">
              <span>{localNom}</span>
              <span>{visiteurNom}</span>
            </div>
            <div className="stats-match__body">
              <hr />
              {actionFiltered
                .filter((action) => 
                  !action.type.includes('carton') && 
                  !action.type.includes('debut_match') && 
                  !action.type.includes('remplacement') &&
                  (action.localActions.length > 0 || action.visiteurActions.length)
                )
                .map(({ id, type, localActions, visiteurActions }) => (
                  <RencontreStatRow
                    key={`statbox_${type}`}
                    localActionsNumber={localActions.length}
                    visiteurActionNumber={visiteurActions.length}
                    localActions={localActions}
                    visiteurActions={visiteurActions}
                    actionType={type}
                    isLocal={isLocal}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { rencontre } = this.props;

    if (!rencontre) {
      return <Loading />;
    }

    const { actions, actions: actionFiltered } = this.state;
    const { localJoueurs } = rencontre;
    const { scoreVis, scoreLoc } = getMidMatchScore(actions);

    const hasActions = actionFiltered
      .filter((action) => action.localActions.length !== 0 || action.visiteurActions.length !== 0).length > 0;

    const hasBegin = actions
      .filter((action) => action.type === 'debut_match' && action.globalActions.length !== 0).length > 0;

    return (
      <>
        <div className="row">
          <div className="col col-lg-!on9">
            {hasBegin &&
            <RencontreHighlights
              actions={actions}
              localScoreMid={scoreLoc}
              visiteurScoreMid={scoreVis}
            />}
            {!hasBegin &&
            <p>{`Cette rencontre n'a pas encore commencé.`}</p>}
          </div>
        </div>
        {hasActions && this._renderStats()}
        {localJoueurs && localJoueurs.length > 0 && <RencontreTeam />}
      </>
    );
  }
}

export default withHelmet(RencontreGeneralLive)((Helmet, pageProps) => {
  const { rencontre } = pageProps;
  const teamA = rencontre.localStructure.nom;
  const teamB = rencontre.visiteurStructure.nom;

  const meta = getMetaBySlug('match/en-direct');

  return (
    <Helmet>
      <title>{meta.title(teamA, teamB)}</title>
      <meta name={'description'} content={meta.description()} />
      {
        meta.description()
        ? <meta name={'keywords'} content={meta.keywords(teamA, teamB)} />
        : ''
      }
      <meta property={'og:title'} content={meta.title(teamA, teamB)} />
      {
        meta.description()
        ? <meta property={'og:description'} content={meta.description()} />
        : ''
      }
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
